"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import { Star, User } from "lucide-react";

export default function Testimonials() {
  const [currentSlide, setCurrentSlide] = useState(0);

  const testimonials = [
    {
      name: 'Darién García',
      company: 'Gestoría Confías S.R.L.',
      text: 'Kern ha transformado por completo la forma en que gestionamos la contabilidad. Su plataforma en la nube nos permite colaborar en tiempo real sin importar dónde estemos, optimizando nuestros tiempos de respuesta.',
    },
    {
      name: 'María López',
      company: 'Emprendedora MiPyme',
      text: 'Como dueña de negocio, buscaba algo simple pero potente. Kern superó mis expectativas; la interfaz es intuitiva y el soporte técnico siempre está ahí para ayudarme con rapidez y profesionalismo.',
    },
    {
      name: 'Juan Martínez',
      company: 'Contador Independiente',
      text: 'La precisión y los reportes detallados que genera Kern son fundamentales para mi trabajo diario. Es, sin duda, la herramienta más completa que he utilizado para el mercado nacional.',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <section id="testimonios" className="relative h-100 px-6 overflow-hidden flex flex-col justify-center">
      {/* Background Image with Dark Overlay */}
      <div className="absolute inset-0 z-0">
        <Image
          src="/start/testimonials/bg-vecteezy.webp"
          alt="Testimonials Background"
          fill
          className="object-cover"
        />
        <div className="absolute inset-0 bg-[#1e2c6e]/85 mix-blend-multiply" />
      </div>

      <div className="max-w-4xl w-full mx-auto relative z-10 flex flex-col items-center">
        <div className="text-center mb-6 text-white px-2">
          <h2 className="text-2xl md:text-3xl font-black mb-1 leading-tight">
            Ellos confían en Kern
          </h2>
          <p className="text-xs md:text-sm text-slate-300 font-medium leading-relaxed opacity-80">
            Empresas que eligieron nuestra plataforma.
          </p>
        </div>

        {/* Carousel Container */}
        <div className="relative overflow-hidden mb-6 w-full max-w-2xl">
          <div 
            className="flex transition-transform duration-1000 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {testimonials.map((testimonial, index) => (
              <div key={index} className="w-full shrink-0 px-4">
                <Card className="bg-white/95 backdrop-blur-sm border-0 rounded-3xl shadow-2xl p-1 w-full mx-auto overflow-hidden">
                  <CardContent className="py-6 px-10 text-center flex flex-col items-center">
                    <div className="flex items-center gap-6 mb-4 w-full justify-center">
                      <div className="w-14 h-14 rounded-full bg-[#1e2c6e] flex items-center justify-center shadow-lg shrink-0">
                        <User className="text-white w-7 h-7" />
                      </div>
                      <div className="text-left">
                        <h3 className="font-black text-[#1e2c6e] text-lg leading-tight">
                          {testimonial.name}
                        </h3>
                        <p className="text-slate-500 font-bold text-xs">
                          {testimonial.company}
                        </p>
                        <div className="flex gap-1 mt-1 text-yellow-400">
                          {[...Array(5)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </div>
                      </div>
                    </div>

                    <p className="text-slate-700 text-sm md:text-base leading-relaxed font-medium italic max-w-2xl">
                      &quot;{testimonial.text}&quot;
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>

        {/* Carousel Indicators (Dots) */}
        <div className="flex justify-center gap-3">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 shadow-md hover:cursor-pointer ${
                currentSlide === index 
                  ? "bg-white scale-125" 
                  : "bg-white/30 hover:bg-white/60"
              }`}
              aria-label={`Ir al testimonio ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
